import clipping from 'polygon-clipping';
import { stairHoles } from './stairs.js';

const cache = new WeakMap();
export const areaEnvironment = a => a.environment === 'outdoor' ? 'outdoor' : 'indoor';
const ringArea = points => Math.abs(points.reduce((sum, p, i) => { const q = points[(i + 1) % points.length]; return sum + p[0] * q[1] - q[0] * p[1]; }, 0)) / 2;
const polygon = a => [a.pts.map(p => [p.x, p.y])];
const size = a => ringArea(polygon(a)[0]);
const priority = a => areaEnvironment(a) === 'indoor' ? 3 : a.purpose === 'road' ? 2 : a.purpose === 'parking' ? 1 : 0;
const contains = (x, y, pts) => {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > y) !== (b.y > y) && x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
};

// Indoor footprints override roads, roads override parking, and parking
// overrides ordinary ground. Smaller areas refine settings within a category.
export function orderedSiteAreas(S) {
  return S.areas.filter(a => a.kind === 'floor' && a.pts.length >= 3)
    .sort((a, b) => priority(a) - priority(b) || size(b) - size(a));
}
export function areaAt(S, x, y, ordered = orderedSiteAreas(S)) {
  for (let i = ordered.length - 1; i >= 0; i--) if (contains(x, y, ordered[i].pts)) return ordered[i];
  return null;
}
export const environmentAt = (S, x, y, height) => {
  const a = areaAt(S, x, y);
  // Beyond all traced floors is exposed space, including the outside of an
  // upper-storey facade. It does not require a fabricated outdoor floor area.
  return !a || (a.roofHeight > 0 && height > a.roofHeight + 0.2) ? 'outdoor' : areaEnvironment(a);
};
export const outdoorReady = model => ['outdoor', 'both'].includes(model?.environment);
export const suitableAt = (S, model, x, y, height) => environmentAt(S, x, y, height) !== 'outdoor' || outdoorReady(model);
export const hasOutdoor = S => S.areas.some(a => a.kind === 'floor' && areaEnvironment(a) === 'outdoor');
export const siteMode = S => hasOutdoor(S) ? S.areas.some(a => a.kind === 'floor' && areaEnvironment(a) === 'indoor') ? 'Interior + exterior' : 'Exterior site' : 'Interior';

/** Non-overlapping visible surfaces and an exact polygon union area. Used by
 * both renderers and server summaries; coverage still uses independent samples.
 * The signature observes in-place editor mutations and undo replacements. */
export function siteSurfaces(S) {
  const holes = stairHoles(S), key = JSON.stringify([S.areas, holes]), previous = cache.get(S);
  if (previous?.key === key) return previous.value;
  const floors = orderedSiteAreas(S), exclusions = S.areas.filter(a => a.kind === 'exclude' && a.pts.length >= 3).map(polygon).concat(holes.map(pts => [pts.map(p => [p.x, p.y])]));
  let covered = exclusions.length ? clipping.union(...exclusions) : [];
  const layers = [];
  for (const area of floors.slice().reverse()) {
    const shape = polygon(area), polygons = covered.length ? clipping.difference(shape, covered) : clipping.union(shape);
    layers.push({ area, polygons });
    covered = covered.length ? clipping.union(covered, shape) : clipping.union(shape);
  }
  const area = layers.reduce((sum, layer) => sum + layer.polygons.reduce((n, rings) => n + ringArea(rings[0]) - rings.slice(1).reduce((a, ring) => a + ringArea(ring), 0), 0), 0);
  for (const layer of layers) layer.visibleArea = layer.polygons.reduce((sum, rings) => sum + ringArea(rings[0]) - rings.slice(1).reduce((n, r) => n + ringArea(r), 0), 0);
  const value = { layers: layers.reverse(), area: Math.max(0, area) };
  cache.set(S, { key, value }); return value;
}

export const surfaceColor = (a, indoor = '#d6d1c7') => areaEnvironment(a) === 'indoor' ? indoor : a.surface === 'asphalt' ? '#64717c' : a.surface === 'grass' ? '#8ea882' : '#b4b6aa';

/** Configurable markings only; these are reference sizes, not parking-code
 * compliance. Clip the returned lines to the visible parking polygon. */
export function parkingLines(a) {
  if (a.purpose !== 'parking' || a.pts.length < 3) return [];
  const angle = (a.parking?.heading || 0) * Math.PI / 180, c = Math.cos(angle), s = Math.sin(angle);
  const origin = a.pts[0], local = a.pts.map(p => ({ x: (p.x - origin.x) * c + (p.y - origin.y) * s, y: -(p.x - origin.x) * s + (p.y - origin.y) * c }));
  const x0 = Math.min(...local.map(p => p.x)), x1 = Math.max(...local.map(p => p.x)), y0 = Math.min(...local.map(p => p.y)), y1 = Math.max(...local.map(p => p.y));
  const width = Math.max(1, a.parking?.bayWidth ?? 2.5), depth = Math.max(2, a.parking?.bayDepth ?? 5), aisle = Math.max(2, a.parking?.aisleWidth ?? 6);
  const world = (x, y) => ({ x: origin.x + c * x - s * y, y: origin.y + s * x + c * y });
  const out = [];
  for (let y = y0; y + depth <= y1 + 1e-6 && out.length < 2000; y += depth + aisle) {
    out.push([world(x0, y), world(x1, y)]);
    for (let x = x0; x <= x1 && out.length < 2000; x += width) out.push([world(x, y), world(x, y + depth)]);
  }
  return out;
}

/** Split markings at every polygon edge, then keep only visible intervals.
 * This also removes bays beneath indoor footprints and exclusion holes. */
export function clippedParkingLines(area, polygons) {
  return clippedSurfaceLines(parkingLines(area), polygons);
}

/** Roads are editable ground polygons. Markings use their centre, heading and
 * reference lane width; neighbouring sections can represent bends/junctions. */
export function roadLines(a) {
  if (a.purpose !== 'road' || a.pts.length < 3 || a.road?.markings === false) return [];
  const c = Math.cos((a.road?.heading || 0) * Math.PI / 180), s = Math.sin((a.road?.heading || 0) * Math.PI / 180);
  const pts = a.pts.map(p => ({ x: p.x * c + p.y * s, y: -p.x * s + p.y * c }));
  const x0 = Math.min(...pts.map(p => p.x)), x1 = Math.max(...pts.map(p => p.x)), mid = (Math.min(...pts.map(p => p.y)) + Math.max(...pts.map(p => p.y))) / 2;
  const width = Math.max(2, a.road?.laneWidth ?? 3.2), lanes = Math.min(6, Math.max(1, Math.round(a.road?.lanes ?? 2)));
  const world = (x, y) => ({ x: x * c - y * s, y: x * s + y * c });
  const out = [], add = (x, y, u, v) => { if (out.length < 2000) out.push([world(x, y), world(u, v)]); };
  for (let lane = 1; lane < lanes; lane++) {
    const y = mid + (lane - lanes / 2) * width;
    for (let x = x0; x < x1 && out.length < 2000; x += 5) add(x, y, Math.min(x + 2.5, x1), y);
  }
  for (const side of [-1, 1]) add(x0, mid + side * lanes * width / 2, x1, mid + side * lanes * width / 2);
  for (let lane = 0; lane < lanes; lane++) {
    const y = mid + (lane + 0.5 - lanes / 2) * width, sign = a.road?.direction === 'one-way' || lane >= lanes / 2 ? 1 : -1;
    for (let x = x0 + 4; x < x1 - 2 && out.length < 2000; x += 14) {
      add(x - sign, y, x + sign, y); add(x + sign, y, x + sign * 0.35, y - 0.4); add(x + sign, y, x + sign * 0.35, y + 0.4);
    }
  }
  return out;
}

export const clippedGroundMarkings = (area, polygons) => clippedSurfaceLines(area.purpose === 'road' ? roadLines(area) : parkingLines(area), polygons);

export function clippedSurfaceLines(lines, polygons) {
  const out = [], rings = polygons.flat();
  const inRing = (p, ring) => {
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = ring[i - 1], [u, v] = ring[i];
      if (Math.abs((p.x - x) * (v - y) - (p.y - y) * (u - x)) < 1e-7 && p.x >= Math.min(x, u) - 1e-8 && p.x <= Math.max(x, u) + 1e-8 && p.y >= Math.min(y, v) - 1e-8 && p.y <= Math.max(y, v) + 1e-8) return true;
    }
    return contains(p.x, p.y, ring.map(([x, y]) => ({ x, y })));
  };
  for (const [a, b] of lines) {
    const dx = b.x - a.x, dy = b.y - a.y, cuts = [0, 1];
    for (const ring of rings) for (let i = 1; i < ring.length; i++) {
      const [x, y] = ring[i - 1], ex = ring[i][0] - x, ey = ring[i][1] - y, den = dx * ey - dy * ex;
      if (Math.abs(den) < 1e-9) continue;
      const t = ((x - a.x) * ey - (y - a.y) * ex) / den, u = ((x - a.x) * dy - (y - a.y) * dx) / den;
      if (t > 0 && t < 1 && u >= 0 && u <= 1) cuts.push(t);
    }
    cuts.sort((a, b) => a - b);
    const point = t => ({ x: a.x + t * dx, y: a.y + t * dy });
    for (let i = 1; i < cuts.length; i++) {
      if (cuts[i] - cuts[i - 1] < 1e-8) continue;
      const mid = point((cuts[i] + cuts[i - 1]) / 2);
      if (polygons.some(poly => inRing(mid, poly[0]) && !poly.slice(1).some(r => inRing(mid, r)))) out.push([point(cuts[i - 1]), point(cuts[i])]);
    }
  }
  return out;
}
